import { Modal, Button, Select } from 'antd';
import { useEffect, useState, useRef, useMemo } from 'react';
import { MicrophoneLevel } from '../modules/ambient_solo/components/baseComponents/MicrophoneLevel';
import { MicOn, SettingsMicIcon } from 'src/icons';
import { Progress, Tooltip } from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { Bars } from 'react-loader-spinner';
import AudioPlayer from 'src/components/baseComponents/AudioStream';
import {
  SAMPLE_RATE,
  // detectMicrophoneSampleRate,
} from 'src/modules/ambient_solo/utils/listen';
import { logger } from 'src/services';

const MIC_TEST_DURATION = 30; // seconds

function convertChunksToWavBlobUrl(chunks, sampleRate = SAMPLE_RATE) {
  // Create WAV header
  const numChannels = 1;
  const bitsPerSample = 16;
  const dataLength = chunks.reduce((acc, chunk) => acc + chunk.length * 2, 0);

  const header = new ArrayBuffer(44);
  const view = new DataView(header);

  // "RIFF" chunk descriptor
  view.setUint8(0, 'R'.charCodeAt(0));
  view.setUint8(1, 'I'.charCodeAt(0));
  view.setUint8(2, 'F'.charCodeAt(0));
  view.setUint8(3, 'F'.charCodeAt(0));
  view.setUint32(4, 36 + dataLength, true);
  view.setUint8(8, 'W'.charCodeAt(0));
  view.setUint8(9, 'A'.charCodeAt(0));
  view.setUint8(10, 'V'.charCodeAt(0));
  view.setUint8(11, 'E'.charCodeAt(0));

  // "fmt " sub-chunk
  view.setUint8(12, 'f'.charCodeAt(0));
  view.setUint8(13, 'm'.charCodeAt(0));
  view.setUint8(14, 't'.charCodeAt(0));
  view.setUint8(15, ' '.charCodeAt(0));
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
  view.setUint16(32, numChannels * (bitsPerSample / 8), true);
  view.setUint16(34, bitsPerSample, true);

  // "data" sub-chunk
  view.setUint8(36, 'd'.charCodeAt(0));
  view.setUint8(37, 'a'.charCodeAt(0));
  view.setUint8(38, 't'.charCodeAt(0));
  view.setUint8(39, 'a'.charCodeAt(0));
  view.setUint32(40, dataLength, true);

  // Convert array chunks to Int16Array buffers for WAV format
  const audioBuffers = chunks.map((chunk) => {
    const buffer = new ArrayBuffer(chunk.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < chunk.length; i++) {
      view.setInt16(i * 2, chunk[i], true); // little-endian
    }
    return buffer;
  });

  // Combine header with audio data
  return URL.createObjectURL(
    new Blob([header, ...audioBuffers], { type: 'audio/wav' })
  );
}

const MicSettings = ({ onSelectionChange, selectedMicrophone, disabled }) => {
  const [open, setOpen] = useState(false);
  const [microphones, setMicrophones] = useState([]);
  const [micLevel, setMicLevel] = useState(1);
  const [stream, setStream] = useState(null);
  // const detectedSampleRate = SAMPLE_RATE;
  const [detectedSampleRate, setDetectedSampleRate] = useState();

  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;

  const [recordingTimer, setRecordingTimer] = useState(0);
  const recordingTimerRef = useRef(recordingTimer);
  recordingTimerRef.current = recordingTimer;

  const [recordingChunks, setRecordingChunks] = useState([]);

  const audioUrl = useMemo(() => {
    if (!recordingChunks.length || isRecording) {
      return null;
    }

    return convertChunksToWavBlobUrl(recordingChunks, detectedSampleRate);
  }, [recordingChunks, isRecording, detectedSampleRate]);

  useEffect(() => {
    const getMicrophones = async () => {
      if (!open) {
        return;
      }
      try {
        // Request microphone permission first to get device labels
        await navigator.mediaDevices.getUserMedia({ audio: true });

        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputDevices = devices.filter(
          (device) => device.kind === 'audioinput'
        );
        setMicrophones(audioInputDevices);
        if (
          !audioInputDevices.some(
            (device) => device.deviceId === selectedMicrophone
          )
        ) {
          onSelectionChange(audioInputDevices[0].deviceId);
        }
      } catch (error) {
        console.error('Error accessing microphones:', error);
      }
    };

    getMicrophones();
    navigator.mediaDevices.addEventListener('devicechange', getMicrophones);
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        getMicrophones
      );
    };
  }, [onSelectionChange, open, selectedMicrophone]);

  useEffect(() => {
    const enableMicStream = async () => {
      const constraints = {
        audio: {
          deviceId: { exact: selectedMicrophone },
        },
      };

      try {
        const newStream =
          await navigator.mediaDevices.getUserMedia(constraints);

        // Detect and store the sample rate
        // try {
        //   const sampleRate = await detectMicrophoneSampleRate(newStream);
        //   setDetectedSampleRate(sampleRate);
        // } catch (error) {
        //   logger.warn(
        //     'Failed to detect microphone sample rate, using default:',
        //     error
        //   );
        //   setDetectedSampleRate(SAMPLE_RATE);
        // }

        setStream(newStream);
      } catch (error) {
        logger.error('Error accessing microphone:', error);
        setStream(null);
        // setDetectedSampleRate(SAMPLE_RATE);
      }
    };

    if (selectedMicrophone && open) {
      enableMicStream();
    } else {
      // Clean up existing stream when closing or no microphone selected
      setStream((currentStream) => {
        if (currentStream) {
          currentStream.getTracks().forEach((track) => track.stop());
        }
        return null;
      });
    }
  }, [selectedMicrophone, open]);

  useEffect(() => {
    const streamLocal = stream;
    let audioContext;
    let audioWorkletNode;
    let input;
    let isSetupComplete = false;

    const handleAudioData = (event) => {
      if (event.data.type === 'audioData') {
        setMicLevel(event.data.micLevel);

        if (event.data.audio && isRecordingRef.current) {
          // The audio processor already converts to int16 PCM format
          // Just store the array directly as it's already in the correct format
          setRecordingChunks((chunks) => [...chunks, event.data.audio]);
        }
      }
    };

    const setupAudioWorklet = async () => {
      if (streamLocal) {
        try {
          // Create an AudioContext with the detected sample rate
          audioContext = new (window.AudioContext || window.webkitAudioContext)(
            {
              latencyHint: 'interactive',
            }
          );

          setDetectedSampleRate(audioContext.sampleRate);
          // Load the AudioWorklet module
          await audioContext.audioWorklet.addModule('/audio-processor.js');

          // Create AudioWorkletNode
          audioWorkletNode = new AudioWorkletNode(
            audioContext,
            'audio-processor'
          );
          input = audioContext.createMediaStreamSource(streamLocal);

          // Set up message handler for audio data from worklet
          audioWorkletNode.port.onmessage = handleAudioData;

          // Connect the audio graph
          input.connect(audioWorkletNode);
          audioWorkletNode.connect(audioContext.destination);

          // Resume the audio context
          await audioContext.resume();

          // Mark setup as complete
          isSetupComplete = true;
        } catch (error) {
          logger.error('Error setting up AudioWorklet:', error);
        }
      }
    };

    // Wait for setup to complete before allowing cleanup
    setupAudioWorklet();

    return () => {
      // Only cleanup if setup was completed to avoid race conditions
      if (isSetupComplete) {
        if (audioWorkletNode) {
          audioWorkletNode.port.onmessage = null;
          audioWorkletNode.disconnect();
        }

        // Disconnect input
        if (input) {
          input.disconnect();
        }

        // Stop all tracks in the stream
        if (streamLocal) {
          streamLocal.getTracks().forEach((track) => track.stop());
        }

        // Close audio context if it exists and isn't already closed
        if (audioContext && audioContext.state !== 'closed') {
          audioContext.close();
        }
      }
    };
  }, [stream]);

  useEffect(() => {
    let intervalId;
    if (isRecording) {
      intervalId = setInterval(() => {
        setRecordingTimer((prev) => prev + 1);
        if (recordingTimerRef.current >= MIC_TEST_DURATION) {
          setIsRecording(false);
        }
      }, 1000);
    } else {
      setRecordingTimer(0);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isRecording]);

  const renderMicTest = () => {
    if (audioUrl) {
      return (
        <div className="flex items-center gap-2 mt-4">
          <AudioPlayer audioUrl={audioUrl} />
          <Tooltip title="Reset" placement="bottom">
            <CloseOutlined
              className="cursor-pointer"
              onClick={() => {
                setIsRecording(false);
                setRecordingChunks([]);
              }}
            />
          </Tooltip>
        </div>
      );
    }

    if (isRecording) {
      return (
        <div className="flex flex-col justify-center items-center gap-2 mt-4">
          <Progress
            type="circle"
            strokeColor="#00d090"
            percent={(recordingTimer / MIC_TEST_DURATION) * 100}
            size={80}
            format={() => (
              <div className="flex flex-col justify-center items-center">
                <p className="text-gray-500 text-lg">{recordingTimer}s</p>
                <Bars height="8" width="8" color="#00d090" />
              </div>
            )}
          />
          <div className="flex gap-3 items-center">
            <Tooltip title="Play" placement="bottom">
              <CheckOutlined
                className="cursor-pointer"
                onClick={() => {
                  setIsRecording(false);
                }}
              />
            </Tooltip>
            <Tooltip title="Stop recording" placement="bottom">
              <CloseOutlined
                className="cursor-pointer"
                onClick={() => {
                  setIsRecording(false);
                  setRecordingChunks([]);
                }}
              />
            </Tooltip>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <SettingsMicIcon disabled={disabled} onClick={() => setOpen(true)} />
      <Modal
        title="Microphone Settings"
        centered={true}
        footer={
          <div className="flex justify-end gap-2 mt-8">
            {!isRecording && !audioUrl && (
              <Button
                className="ih-button-primary left-0 mr-auto"
                onClick={() => setIsRecording(true)}
              >
                Test microphone
              </Button>
            )}
            <Button
              className="ih-button-secondary"
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </div>
        }
        open={open}
        onCancel={() => setOpen(false)}
        afterOpenChange={setOpen}
      >
        <div className="flex flex-col gap-4">
          <Select
            disabled={isRecording}
            loading={microphones.length === 0}
            value={selectedMicrophone}
            onChange={(deviceId) => onSelectionChange(deviceId)}
          >
            {microphones.map((microphone) => (
              <Select.Option
                key={microphone.deviceId}
                value={microphone.deviceId}
              >
                {microphone.label || `Microphone ${microphone.deviceId}`}
              </Select.Option>
            ))}
          </Select>
          <div className="flex items-center gap-2">
            <MicOn />
            <MicrophoneLevel micLevel={micLevel} />
          </div>
        </div>
        {renderMicTest()}
      </Modal>
    </>
  );
};

export { MicSettings };
